import * as React from 'react'
import { fromJSX, Updater, Widget } from 'widgets-for-react'
import { User } from '../../../authentication/auth_widget/authState'
import {
  ComponentDidMount,
  ComponentDidUpdate,
} from '../../../utils/lifecycleMethods'
import { CurrentPage } from '../../../utils/routes'
import { Pages } from '../routes/routesState'
import { isDifferentPage } from '../routes/routesUtils'
import { getToken } from './hubspotTrackingApi'
import {
  HubspotTrackingState,
  hubspotTrackingStateUpdaters,
} from './hubspotTrackingState'

declare global {
  interface Window {
    HubSpotConversations?: {
      widget: {
        load: () => any
        refresh: () => any
      }
    }
    hsConversationsSettings?: {
      loadImmediately: boolean
      identificationEmail?: string
      identificationToken?: string
    }
    hsConversationsOnReady: (() => any)[]
    _hsq: any[],
    HubSpotCallsToActions?: {
      refresh: () => any
    }
  }
}

const loadWidget = (): void => {
  if (window.HubSpotConversations) {
    window.HubSpotConversations!.widget.load()
  } else {
    window.hsConversationsOnReady = [
      () => window.HubSpotConversations!.widget.load(),
    ]
  }
}

const refreshWidget = (): void => {
  window.HubSpotConversations!.widget.refresh()
}

const initializeTracking = async (
  state: HubspotTrackingState,
  user: User | undefined,
  onDone: (_: Updater<HubspotTrackingState>) => void
): Promise<void> => {
  if (!state.busy) {
    onDone((s) => ({ ...s, busy: true }))
    let identified = state.identified
    let widgetLoaded = state.widgetLoaded
    if (user != null && user.email != null) {
      identify(user.email)
      const tokenResponse = await getToken(user)
      if (tokenResponse.token != null) {
        window.hsConversationsSettings = {
          loadImmediately: false,
          identificationEmail: user.email,
          identificationToken: tokenResponse.token,
        }
        identified = true
      }
    } else {
      identified = false
    }
    if (!state.widgetLoaded) {
      loadWidget()
      widgetLoaded = true
    } else {
      refreshWidget()
    }
    onDone((s) => ({ ...s, identified, widgetLoaded, busy: false }))
  }
}

const identify = (email: string): void => {
  console.log('identifying user')
  const _hsq = (window._hsq = window._hsq || [])
  _hsq.push(['identify', { email }])
  trackPageView()
}

const trackPageView = (): void => {
  console.log('tracking page view ' + window.location.pathname + window.location.search)

  const _hsq = (window._hsq = window._hsq || [])
  _hsq.push(['setPath', window.location.pathname + window.location.search])
  _hsq.push(['trackPageView'])
}

const trackPageViewCTA = (): void => {
  if (window && typeof window["HubSpotCallsToActions"] !== "undefined") {
    window.HubSpotCallsToActions.refresh()
  }
}

export const hubspotTrackingWidget = (
  state: HubspotTrackingState,
  page: CurrentPage<Pages>,
  user?: User
): Widget<Updater<HubspotTrackingState>> =>
  fromJSX((onDone) => {
    const settings = window.hsConversationsSettings

    // If there was a user, but they have logged out. Reset
    if (settings && settings.identificationToken != null && user == null) {
      const { identificationEmail, identificationToken, ...newSettings } =
        settings
      window.hsConversationsSettings = newSettings
      onDone(hubspotTrackingStateUpdaters.resetIdentity)
    }

    const callTrackingCode = () => {
        if (state && state.identified) {
            trackPageView()
        }

        trackPageViewCTA()
    }

    return (
      <>
        <>
          <ComponentDidUpdate
            value={page}
            onUpdate={(oldPage, newPage) => {
              if (isDifferentPage(oldPage, newPage)) {
                callTrackingCode()
              }
            }}
          />
          <ComponentDidMount onMount={callTrackingCode} />
        </>
        <>
          <ComponentDidUpdate
            value={user}
            onUpdate={(oldUser, newUser) => {
              if (
                (!oldUser && newUser) ||
                (oldUser && !newUser) ||
                (oldUser && newUser && oldUser.id !== newUser.id)
              )
                initializeTracking(state, user, onDone)
            }}
          />
          <ComponentDidMount
            onMount={() => initializeTracking(state, user, onDone)}
          />
        </>
      </>
    )
  })
